<template>
  <div class="layout-topbar">
    <div class="layout-topbar-left">
      <router-link to="/" class="layout-topbar-logo">
        <img :src="logoUrl" :alt="companyName" />
      </router-link>

      <button
        type="button"
        class="p-link layout-menu-button layout-topbar-button"
        aria-label="Open navigation"
        :aria-expanded="isSidebarActive"
        aria-controls="app-navigation"
        @click="onMenuToggle()"
      >
        <i class="pi pi-bars"></i>
      </button>
    </div>

    <div class="layout-topbar-right">
      <button
        type="button"
        class="topbar-command"
        aria-label="Open global search"
        title="Global search"
        @click="openGlobalSearch"
      >
        <i class="pi pi-search" />
        <span>Search workspace</span>
        <kbd>Ctrl K</kbd>
      </button>

      <div class="layout-topbar-menu">
        <button
          type="button"
          class="p-link layout-topbar-button"
          aria-label="Open profile menu"
          title="Profile"
          @click="toggleSelectAction"
        >
          <i class="pi pi-user"></i>
        </button>
        <OverlayPanel
          ref="SelectActionSection"
          class="customer-action-overlay custom-table-action-w"
          :breakpoints="{ '640px': '210px' }"
        >
          <div class="SelectActionSection">
            <ul>
              <li>
                <i class="pi pi-user"></i>
                <span>Profile</span>
              </li>
              <li @click="logOutUser">
                <i class="pi pi-sign-out"></i>
                <span>Logout</span>
              </li>
            </ul>
          </div>
        </OverlayPanel>
      </div>

      <button
        type="button"
        class="p-link layout-topbar-menu-button layout-topbar-button"
        aria-label="More actions"
        :aria-expanded="topbarMenuActive"
        @click="onTopBarMenuButton()"
      >
        <i class="pi pi-ellipsis-v"></i>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useLayout } from '@/layout/composables/layout';
import { useCompanyBrand } from '@/composables/useCompanyBrand';
import OverlayPanel from 'primevue/overlaypanel';
import GlobalService from '@/service/GlobalService';
import emitter from '@/utils/eventBus';
import { logout } from '@/utils/logout';
const GlobalServiceItem = ref(new GlobalService());

const { onMenuToggle, layoutConfig, isSidebarActive } = useLayout();
const { companyName, companyLogoUrl } = useCompanyBrand();

const logoUrl = computed(() => {
  if (companyLogoUrl.value) return companyLogoUrl.value;
  return `/layout/images/${layoutConfig.darkTheme.value ? 'logo-white' : 'logo-dark'}.svg`;
});

const SelectActionSection = ref(OverlayPanel);
const outsideClickListener = ref(null);
const topbarMenuActive = ref(false);

const toggleSelectAction = (event) => {
  SelectActionSection.value?.toggle(event);
};

onMounted(() => {
  bindOutsideClickListener();
});

onBeforeUnmount(() => {
  unbindOutsideClickListener();
});

const logOutUser = () => {
  GlobalServiceItem.value
    .logOut()
    .then(() => {
      logout();
    })
    .catch((error) => {
      emitter.emit('toast-axios', error);
    });
};

const onTopBarMenuButton = () => {
  topbarMenuActive.value = !topbarMenuActive.value;
};
const bindOutsideClickListener = () => {
  if (!outsideClickListener.value) {
    outsideClickListener.value = (event) => {
      if (isOutsideClicked(event)) {
        topbarMenuActive.value = false;
      }
    };
    document.addEventListener('click', outsideClickListener.value);
  }
};
const unbindOutsideClickListener = () => {
  if (outsideClickListener.value) {
    document.removeEventListener('click', outsideClickListener.value);
    outsideClickListener.value = null;
  }
};
const isOutsideClicked = (event) => {
  if (!topbarMenuActive.value) return;

  const sidebarEl = document.querySelector('.layout-topbar-menu');
  const topbarEl = document.querySelector('.layout-topbar-menu-button');

  return !sidebarEl || !topbarEl
    ? true
    : !(
        sidebarEl.isSameNode(event.target) ||
        sidebarEl.contains(event.target) ||
        topbarEl.isSameNode(event.target) ||
        topbarEl.contains(event.target)
      );
};

const openGlobalSearch = () => emitter.emit('open-global-search');
</script>

<style lang="scss" scoped>
.topbar-command {
  display: inline-flex;
  gap: 0.55rem;
  align-items: center;
  min-height: 2.5rem;
  padding: 0 0.65rem;
  border: 1px solid var(--surface-border);
  border-radius: 10px;
  color: var(--text-color-secondary);
  background: var(--surface-ground);
  cursor: pointer;
}
.topbar-command:hover {
  border-color: var(--primary-color);
  color: var(--text-color);
}
.topbar-command kbd {
  padding: 0.1rem 0.3rem;
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  background: var(--surface-card);
  font-size: 0.68rem;
}
@media (max-width: 720px) {
  .topbar-command span,
  .topbar-command kbd {
    display: none;
  }
}
</style>
